import {
  BulkMCellEmployeeLogin,
  BulkMCellEmployeeLogout,
  BulkMCellLoginResult,
  JobFunction,
  JobFunctionInfo,
  JobFunctionTagDto,
  JobFunctionTagInfo,
  MCellEmployeeInMCellFilter,
  MCellEmployeeJobStationExperienceLoggedIn,
  MCellEmployeeLoggedInFilter,
  MCellEmployeeLogin,
  MCellEmployeeLogout,
  MCellLoginDto,
  MCellEmployeeSearchDto,
} from './types';
import axiosInstance from '../../api';
import {
  MCellEmployee,
  MCellEmployeeFilterValue,
  MCellEmployeeInMCell,
  JobFunctionHistoryDto,
  JobFunctionTagHistoryDto,
  JobStationHistoryDto,
  MCellEmployeeMCellHistory,
  MCellEmployeeInput,
  MCellEmployeeListDto,
  EmployeeListDto,
} from '../resourceTracking/types';
import { MCellLoginReport, MCellLoginReportFilter } from './types';

/**
 * API call to create a Job Function
 * @param jobFunctionInfo
 */
export const createJobFunction = async (
  jobFunctionInfo: JobFunctionInfo,
): Promise<JobFunction> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/jobFunction',
    jobFunctionInfo,
  );
  return response.data;
};

/**
 * API call to get a list of Job Functions
 * @param inventoryCenterId
 */
export const getJobFunctions = async (
  inventoryCenterId: string,
): Promise<JobFunction[]> => {
  const response = await axiosInstance.get('/api/MCellEmployee/jobFunction', {
    params: { inventoryCenterId },
  });
  return response.data;
};

/**
 * API call to update a Job Function
 * @param info
 */
export const updateJobFunction = async (
  info: JobFunctionInfo,
): Promise<JobFunction> => {
  const response = await axiosInstance.post(
    `/api/MCellEmployee/jobFunction/${info.id}`,
    info,
  );
  return response.data;
};

/**
 * API call to delete a Job Function
 * @param id
 */
export const deleteJobFunction = async (id: string) => {
  await axiosInstance.delete(`/api/MCellEmployee/jobFunction/${id}`);
  return id;
};

/**
 * API call to get a logged in MCell Employees
 * @param filter
 */
export const getMCellEmployeesLoggedIn = async (
  filter: MCellEmployeeLoggedInFilter,
): Promise<MCellEmployee[]> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellEmployeesLoggedIn',
    {
      params: filter,
    },
  );
  return response.data;
};

/**
 * API call to get a MCell Employees in a MCell. Logged in, assigned to station in cell, is home cell etc
 * @param filter
 */
export const getMCellEmployeesInMCell = async (
  filter: MCellEmployeeInMCellFilter,
): Promise<MCellEmployeeInMCell[]> => {
  const { mCellId, inventoryCenterId, mCellCollectionId, ...rest } = filter;
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellEmployeesInMCell',
    {
      params: {
        mCellId,
        inventoryCenterId,
        mCellCollectionId,
        ...rest,
      },
    },
  );
  return response.data;
};

/**
 * API call to get MCell Login Last Updated date
 */
export const getMCellLoginLastUpdatedDate = async (): Promise<string> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellLoginLastUpdatedDate',
  );
  return response.data;
};

/**
 * API call to log in an MCell Employee.
 * @param mCellEmployeeLogin - The login information of the MCell Employee.
 * @returns A Promise that resolves to an MCellLoginDto object containing the login details of the MCell Employee.
 */
export const mCellLogin = async (
  mCellEmployeeLogin: MCellEmployeeLogin,
): Promise<MCellLoginDto> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/mCellLogin',
    mCellEmployeeLogin,
  );
  return response.data;
};

/**
 * API call to log out an MCell Employee.
 * @param mCellEmployeeLogout - The logout information of the MCell Employee.
 * @returns A Promise that resolves to an MCellLoginDto object containing the logout details of the MCell Employee.
 */
export const mCellLogout = async (
  mCellEmployeeLogout: MCellEmployeeLogout,
): Promise<MCellLoginDto> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/mCellLogout',
    mCellEmployeeLogout,
  );
  return response.data;
};

/**
 * API call to log in multiple MCell Employees in bulk.
 * @param {BulkMCellEmployeeLogin} request - The bulk login request containing the details of employees to log in.
 * @returns {Promise<BulkMCellLoginResult>} A Promise that resolves to the result of the bulk login operation.
 */
export const bulkMCellLogin = async (
  request: BulkMCellEmployeeLogin,
): Promise<BulkMCellLoginResult> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/mCellLogin/bulk',
    request,
  );
  return response.data;
};

/**
 * API call to log out multiple MCell Employees in bulk.
 * @param {BulkMCellEmployeeLogout} request - The bulk logout request containing the details of employees to log out.
 * @returns {Promise<BulkMCellLoginResult>} A Promise that resolves to the result of the bulk logout operation.
 */
export const bulkMCellLogout = async (
  request: BulkMCellEmployeeLogout,
): Promise<BulkMCellLoginResult> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/mCellLogout/bulk',
    request,
  );
  return response.data;
};

/**
 * API call to get MCell Employees for use in select components.
 * When includeAllCenters is true, returns employees from all Inventory Centers with center
 * metadata populated for client-side grouping. inventoryCenterId is always required.
 * @param inventoryCenterId - The current Inventory Center ID
 * @param includeAllCenters - When true, employees from all centers are returned
 */
export const getMCellEmployeeSelection = async (
  inventoryCenterId: string,
  includeAllCenters = false,
): Promise<MCellEmployee[]> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellEmployeeSelection',
    {
      params: { inventoryCenterId, includeAllCenters },
    },
  );
  return response.data;
};

/**
 * API call to create a Job Function Tag
 * @param info
 */
export const createJobFunctionTag = async (
  info: JobFunctionTagInfo,
): Promise<JobFunctionTagDto> => {
  const response = await axiosInstance.post(
    '/api/MCellEmployee/jobFunctionTag',
    JSON.stringify(info.title),
  );
  return response.data;
};

/**
 * API call to get a list of Job Function Tags
 */
export const getJobFunctionTags = async (): Promise<JobFunctionTagDto[]> => {
  const response = await axiosInstance.get('/api/MCellEmployee/jobFunctionTag');
  return response.data;
};

/**
 * API call to update a Job Function Tag
 * @param info
 */
export const updateJobFunctionTag = async (
  info: JobFunctionTagInfo,
): Promise<JobFunctionTagDto> => {
  const response = await axiosInstance.post(
    `/api/MCellEmployee/jobFunctionTag/${info.id}`,
    JSON.stringify(info.title),
  );
  return response.data;
};

/**
 * API call to delete a Job Function Tag
 * @param id
 */
export const deleteJobFunctionTag = async (id: string) => {
  await axiosInstance.delete(`/api/MCellEmployee/jobFunctionTag/${id}`);
  return id;
};

/**
 * API call to get the Job Station History for an MCell Employee
 * @param employeeId
 */
export const getMCellEmployeeJobStationHistory = async (employeeId: string) => {
  const response = await axiosInstance.get<JobStationHistoryDto[]>(
    `/api/MCellEmployee/${employeeId}/jobStationHistory`,
  );
  return response.data;
};
/**
 * API call to get the Job Function History for an MCell Employee
 * @param employeeId
 */
export const getMCellEmployeeJobFunctionHistory = async (
  employeeId: string,
) => {
  const response = await axiosInstance.get<JobFunctionHistoryDto[]>(
    `/api/MCellEmployee/${employeeId}/jobFunctionHistory`,
  );
  return response.data;
};
/**
 * API call to get the Job Function Tag History for an MCell Employee
 * @param employeeId
 */
export const getMCellEmployeeJobFunctionTagHistory = async (
  employeeId: string,
) => {
  const response = await axiosInstance.get<JobFunctionTagHistoryDto[]>(
    `/api/MCellEmployee/${employeeId}/jobFunctionTagHistory`,
  );
  return response.data;
};

/**
 * API call to get the Work Cell History for an MCell Employee
 * @param employeeId
 */
export const getMCellEmployeeHistory = async (employeeId: string) => {
  const response = await axiosInstance.get<MCellEmployeeMCellHistory[]>(
    `/api/MCellEmployee/${employeeId}/mCellHistory`,
  );
  return response.data;
};

/**
 * API call to get MCell Login Report
 * @param filter
 */
export const getMCellLoginReport = async (
  filter: MCellLoginReportFilter,
): Promise<MCellLoginReport[]> => {
  const response = await axiosInstance.get(
    `/api/MCellEmployee/mCellLoginReport`,
    {
      params: filter,
    },
  );
  return response.data;
};

/**
 * API call to get MCell Employee Search Report data
 */
export const getMCellEmployeeSearchReportData = async (
  filter: MCellEmployeeFilterValue,
): Promise<MCellEmployeeSearchDto[]> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellEmployeeReport',
    {
      params: filter,
    },
  );
  return response.data;
};

/**
 * API call to get employee search report CSV data
 */
export const downloadEmployeeReportCsv = async (): Promise<Blob> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/mCellEmployeeReportCsv',
  );
  return new Blob([response.data]);
};

/**
 * API call to get employees with experience in station currently logged into other stations
 * @param jobStationId
 */
export const getJobStationExperiencedEmployeesInOtherStations = async (
  jobStationId: string,
): Promise<MCellEmployeeJobStationExperienceLoggedIn[]> => {
  const response = await axiosInstance.get(
    `/api/MCellEmployee/jobStation/${jobStationId}/experiencedEmployeesInOtherStations`,
  );
  return response.data;
};

/**
 * API call to get employees with related experience in station currently logged into other stations
 * @param jobStationId
 */
export const getJobStationRelatedExperiencedEmployeesInOtherStations = async (
  jobStationId: string,
): Promise<MCellEmployeeJobStationExperienceLoggedIn[]> => {
  const response = await axiosInstance.get(
    `/api/MCellEmployee/jobStation/${jobStationId}/relatedExperiencedEmployeesInOtherStations`,
  );
  return response.data;
};

/**
 * API call to trigger a manual refresh of AD Users and MCell Employees
 */
export const triggerManualEmployeeRefresh = async () => {
  await axiosInstance.post('/api/MCellEmployee/triggerFullEmployeeRefresh');
};

/**
 * API call to check the status of the employee refresh
 */
export const checkEmployeeRefreshStatus = async (): Promise<{
  isLeased: boolean;
}> => {
  const response = await axiosInstance.get(
    '/api/MCellEmployee/checkEmployeeRefreshStatus',
  );
  return response.data;
};

/**
 * API call to get all active MCell Employees as DTOs
 */
export const getMCellEmployeesList = async (): Promise<
  MCellEmployeeListDto[]
> => {
  const response = await axiosInstance.get(
    '/api/ResourceTracking/mCellEmployeesList',
  );
  return response.data;
};

/**
 * API call to get all active Employees as DTOs
 */
export const getEmployeesList = async (): Promise<EmployeeListDto[]> => {
  const response = await axiosInstance.get(
    '/api/ResourceTracking/employeesList',
  );
  return response.data;
};

/**
 * API call to create a new MCellEmployee
 */
export const createMCellEmployee = async (
  mCellEmployeeInput: MCellEmployeeInput,
): Promise<MCellEmployee> => {
  const response = await axiosInstance.post(
    '/api/ResourceTracking/mCellEmployee',
    mCellEmployeeInput,
  );
  return response.data;
};

/**
 * API call to update an existing MCellEmployee
 */
export const updateMCellEmployee = async (
  id: string,
  mCellEmployeeInput: MCellEmployeeInput,
): Promise<MCellEmployee> => {
  const response = await axiosInstance.put(
    `/api/ResourceTracking/mCellEmployee/${id}`,
    mCellEmployeeInput,
  );
  return response.data;
};

/**
 * API call to delete an MCellEmployee (soft delete - sets Active = false)
 */
export const deleteMCellEmployee = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/api/ResourceTracking/mCellEmployee/${id}`);
};
